/**
 * Transforms a P2P offer from the API
 *
 * @param offer The API offer resembling a P2P offer
 * @returns The P2P offer
 */
export function transformOfferP2P(offer: ApiSchema['Offer'] | ApiSchema['ExchangeOffer']): OfferP2P | undefined {
    const passengerRefs = offer.passengerRefs;
    const tripIds = offer.tripCoverage?.coveredTripIds ?? [];

    // Transform the reservation-related stuff
    const {
        hasEPR,
        isEPRRequired,
        offerParts,
    } = transformOfferReservations(offer);

    return {
        offerId: offer.offerId,
        type: 'P2P',
        tripIds,
        passengerRefs,
        offerParts,
        hasEPR,
        isEPRRequired,
    } satisfies OfferP2P;
}

/**
 * Takes a look at the constellation of admissions + reservations and
 * determines EPR data:
 * - isEPRRequired: true if reservations are mandatory
 * - hasEPR: true if there are any reservations (or only reservations)
 * - offerParts: both admission and reservation offer parts
 */
function transformOfferReservations(offer: ApiSchema['Offer'] | ApiSchema['ExchangeOffer']) {
    let hasEPR: boolean | 'only';
    let isEPRRequired: boolean;
    let admissionOfferParts: OfferPartAdmission[];
    let reservationOfferParts: OfferPartReservation[];

    // EPR Only:
    // There are no admissions, but only reservations
    // This is a "reservation only" product
    if (!offer.admissionOfferParts?.length && offer.reservationOfferParts?.length) {
        isEPRRequired = true;
        hasEPR = 'only';
        admissionOfferParts = [];
        reservationOfferParts = offer.reservationOfferParts?.map(rop => transformOfferPartReservation(rop)) ?? [];
    }
    // EPR Optional:
    // Has admissionOfferParts and separate reservationOfferParts
    // This is a normal P2P product that has optional reservations
    else if (offer.reservationOfferParts?.length) {
        isEPRRequired = false;
        hasEPR = true;
        admissionOfferParts = offer.admissionOfferParts?.map(aop => transformOfferPartAdmission(aop)) ?? [];
        reservationOfferParts = offer.reservationOfferParts?.map(rop => transformOfferPartReservation(rop)) ?? [];
    }
    // EPR Mandatory:
    // Has admissions marked with "isReservationRequired" and
    // the reservations are inside the admissionOfferParts
    else if (offer.admissionOfferParts?.some(a => a.isReservationRequired)) {
        isEPRRequired = true;
        hasEPR = true;
        admissionOfferParts = offer.admissionOfferParts?.map(aop => transformOfferPartAdmission(aop)) ?? [];
        reservationOfferParts = [];
    }
    // No EPR:
    // Has NO reservationOfferParts (only admissionOfferParts)
    // This is a normal P2P product that has no reservations at all
    else {
        isEPRRequired = false;
        hasEPR = false;
        admissionOfferParts = offer.admissionOfferParts?.map(aop => transformOfferPartAdmission(aop)) ?? [];
        reservationOfferParts = [];
    }

    const offerParts = [
        ...admissionOfferParts,
        ...reservationOfferParts,
    ]
        // Sort by passengerRefs to ensure the order is consistent
        .sort((a, b) => {
            const passengerRefsA = JSON.stringify(a.passengerRefs);
            const passengerRefsB = JSON.stringify(b.passengerRefs);

            if (passengerRefsA !== passengerRefsB) {
                return passengerRefsA > passengerRefsB ? 1 : -1;
            }
            return a.id > b.id ? 1 : a.id < b.id ? -1 : 0;
        }) ?? [];

    return {
        hasEPR,
        isEPRRequired,
        offerParts,
    };
}

/**
 * Transforms a reservation offer part (for optional reservations. If mandatory, it's inside the admission offer part)
 */
function transformOfferPartReservation(rop: ApiSchema['ReservationOfferPart']): OfferPartReservation {
    return {
        id: rop.id,
        type: 'ReservationOfferPart',
        summary: rop.summary ?? 'Reservation',
        price: rop.price,
        passengerRefs: rop.passengerRefs,
        promotionCodes: rop.appliedPromotionCodes?.map(transformPromotionCode) ?? [],
        refundDeadline: transformRefundDeadline(rop.afterSalesConditions),
        seatsLeft: rop.numericAvailability ?? 999,
        tripCoverage: {
            tripIds: rop.tripCoverage?.coveredTripIds ?? [],
            legIds: rop.tripCoverage?.coveredLegIds ?? [],
        },
    };
}

/**
 * Transforms an admission offer part
 */
function transformOfferPartAdmission(aop: ApiSchema['AdmissionOfferPart']): OfferPartAdmission {
    return {
        id: aop.id,
        type: 'AdmissionOfferPart',
        summary: aop.summary ?? '',
        passengerRefs: aop.passengerRefs,
        price: aop.price,
        isEPRRequired: aop.isReservationRequired ?? false,
        promotionCodes: aop.appliedPromotionCodes?.map(transformPromotionCode) ?? [],
        refundDeadline: transformRefundDeadline(aop.afterSalesConditions),
        tripCoverage: {
            tripIds: aop.tripCoverage?.coveredTripIds ?? [],
        },

        // For trips with managed reservations, the reservations are included INSIDE the admissionOfferParts
        reservationOfferParts: aop.includedReservations?.map(rop => transformOfferPartReservation(rop)) ?? [],
    };
}
